import { PhoneNumberTypes } from 'Constants/enums';
import { ContactModelV2 } from 'Models/ContactModelV2';
import { mapContactCategory } from 'Modules/contacts/sidePanel/utils/map_contact_category';
import { getContactType } from 'Modules/conversation/createConversation/utils';
import { CallLogContactOption } from '../../types';

/**
 * Maps a list of contacts into autocomplete options for the call logs search.
 *
 * Each option exposes the full contact model so that selecting a contact triggers
 * a search across all its phone numbers and extension numbers, including sourceId
 * for internal contacts.
 *
 * When the user types digits, the description is extended to show the matched
 * phone number and its type label.
 *
 * @param contacts - Flat list of contacts from paginated API results
 * @param inputValue - Current value typed by the user in the search field
 * @returns Array of CallLogContactOption objects ready to be rendered by the Autocomplete
 */
export const buildContactOptions = (
  contacts: ContactModelV2[],
  inputValue: string
): CallLogContactOption[] => {
  const digits = inputValue.replace(/\D/g, '');

  return contacts.map((contact) => {
    const baseDescription = mapContactCategory(contact);
    let description = baseDescription;

    if (digits && contact.phoneNumbers?.length) {
      const matched = contact.phoneNumbers.find((p) =>
        // Normalize before comparing to handle different number formats
        p.number.replace(/\D/g, '').includes(digits)
      );

      if (matched) {
        const formattedNumber = ContactModelV2.formatNumber(matched.number);
        const typeLabel = PhoneNumberTypes.find((t) => t.key === matched.type)?.text;
        const phoneDisplay = typeLabel
          ? `${formattedNumber} [${typeLabel}]`
          : formattedNumber;

        description = `${baseDescription} • ${phoneDisplay}`;
      }
    }

    return {
      value: contact.id,
      label: contact.name ?? '',
      title: contact.name ?? '',
      description,
      avatar: contact.avatarUrl,
      type: getContactType(contact.source),
      phoneNumbers: contact.phoneNumbers,
      // Exposed to allow filtering call logs by all associated phone numbers, extensions
      // and sourceId (for internal contacts).
      selectedContact: contact,
    };
  });
};